'use client'

import type { ReactNode } from 'react'
import type {
  PermissionCheck,
  PermissionMode,
  UseHasPermissions,
} from './permissions'

import { createContext, useContext } from 'react'

// Without a provider (stories, package tests) every permission-gated
// affordance stays hidden.
const DENY_ALL: UseHasPermissions = () => false

const PermissionsContext = createContext<UseHasPermissions>(DENY_ALL)

type PermissionsProviderProps = {
  useHasPermissions: UseHasPermissions
  children: ReactNode
}

/**
 * Injects the host app's permission hook once, so feature packages can gate on
 * grants with `useHasPermissions` without depending on the host's store.
 */
export function PermissionsProvider({
  useHasPermissions,
  children,
}: PermissionsProviderProps): React.ReactElement {
  return (
    <PermissionsContext.Provider value={useHasPermissions}>
      {children}
    </PermissionsContext.Provider>
  )
}

export function useHasPermissions(
  requiredPermissions: PermissionCheck[],
  mode?: PermissionMode,
): boolean | undefined {
  const useInjectedHasPermissions = useContext(PermissionsContext)
  return useInjectedHasPermissions(requiredPermissions, mode)
}
